import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Shield, Trash2, ShieldCheck, ShieldOff, Search, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from '../../i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('admin.usersLoadFailed'));
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/stats/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin.userDeletedSuccess'));
      fetchUsers();
      fetchStats();
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || t('admin.userDeleteFailed'));
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/users/${userId}/toggle-admin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        response.data.is_admin
          ? t('admin.adminGranted')
          : t('admin.adminRevoked')
      );
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error(error.response?.data?.detail || t('admin.operationFailed'));
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const locale = lang === 'es' ? 'es-ES' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl" style={{ color: 'var(--text-secondary)' }}>
          {t('admin.loading')}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            className="p-6 rounded-lg shadow-md"
            style={{ background: 'var(--bg-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {t('admin.totalMembers')}
                </p>
                <p
                  className="text-3xl font-bold mt-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {stats.total}
                </p>
              </div>
              <Users className="w-12 h-12" style={{ color: 'var(--accent)' }} />
            </div>
          </div>

          <div
            className="p-6 rounded-lg shadow-md"
            style={{ background: 'var(--bg-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {t('admin.adminUsers')}
                </p>
                <p
                  className="text-3xl font-bold mt-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {stats.admins}
                </p>
              </div>
              <Shield className="w-12 h-12" style={{ color: 'var(--accent)' }} />
            </div>
          </div>

          <div
            className="p-6 rounded-lg shadow-md"
            style={{ background: 'var(--bg-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {t('admin.regularUsers')}
                </p>
                <p
                  className="text-3xl font-bold mt-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {stats.regular}
                </p>
              </div>
              <Users className="w-12 h-12" style={{ color: 'var(--text-secondary)' }} />
            </div>
          </div>

          <div
            className="p-6 rounded-lg shadow-md"
            style={{ background: 'var(--bg-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {t('admin.last7Days')}
                </p>
                <p
                  className="text-3xl font-bold mt-2"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {stats.recent_signups}
                </p>
              </div>
              <Calendar className="w-12 h-12" style={{ color: 'var(--accent)' }} />
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
            style={{ color: 'var(--text-secondary)' }}
          />
          <Input
            type="text"
            placeholder={t('admin.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div
        className="rounded-lg shadow-md overflow-hidden"
        style={{ background: 'var(--bg-primary)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ background: 'var(--bg-secondary)' }}>
              <tr>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('admin.username')}
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('admin.email')}
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('admin.role')}
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('admin.registrationDate')}
                </th>
                <th
                  className="px-6 py-3 text-right text-sm font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('admin.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {t('admin.noUsersFound')}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-t"
                    style={{ borderColor: 'var(--bg-secondary)' }}
                  >
                    <td
                      className="px-6 py-4"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {user.username}
                    </td>
                    <td
                      className="px-6 py-4"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {user.email}
                    </td>
                    <td className="px-6 py-4">
                      {user.is_admin ? (
                        <Badge className="bg-yellow-600 hover:bg-yellow-700">
                          <Shield className="w-3 h-3 mr-1" />
                          {t('admin.admin')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">{t('admin.user')}</Badge>
                      )}
                    </td>
                    <td
                      className="px-6 py-4"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                          className="flex items-center gap-1"
                        >
                          {user.is_admin ? (
                            <>
                              <ShieldOff className="w-4 h-4" />
                              {t('admin.removeAdmin')}
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-4 h-4" />
                              {t('admin.makeAdmin')}
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.deleteUser')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.deleteUserConfirm')} <strong>{selectedUser?.username}</strong>? {t('admin.deleteUserWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('admin.yesDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
